import { useEffect, useRef, useCallback } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { isTourSkipped, skipTours } from './tourUtils';

const TOUR_COMPLETED_KEY = 'stackguard_manage_scans_tour_completed';

let driverInstance = null;

const getTourSteps = () => [
  // Tab Navigation
  {
    element: '[data-tour="manage-scans-tabs"]',
    popover: {
      title: 'Scan Categories',
      description: 'Use these tabs to navigate between different scan types: Repositories, Storage, SaaS Applications, and Cloud Infrastructure. Each tab allows you to configure and schedule scans for that category.',
      side: 'bottom',
      align: 'start'
    }
  },
  // Repositories Tab
  {
    element: '[data-tour="manage-scans-repositories-tab"]',
    popover: {
      title: 'Repositories Tab',
      description: 'Click here to manage repository scans. Schedule security scans for your GitHub, GitLab, and Bitbucket repositories.',
      side: 'bottom',
      align: 'start'
    }
  },
  // Repositories Panel
  {
    element: '[data-tour="manage-scans-repositories-panel"]',
    popover: {
      title: 'Repository Scan Management',
      description: 'This panel shows all your repositories and allows you to schedule scans, view scan history, and manage scan intervals. Select repositories and configure scan schedules.',
      side: 'left',
      align: 'start'
    }
  },
  // Storage Tab
  {
    element: '[data-tour="manage-scans-storage-tab"]',
    popover: {
      title: 'Storage Tab',
      description: 'Click here to configure storage scanning. Manage scans for S3 buckets, Azure Blob Storage, Google Cloud Storage, and other storage services.',
      side: 'bottom',
      align: 'start'
    }
  },
  // Storage Panel
  {
    element: '[data-tour="manage-scans-storage-panel"]',
    popover: {
      title: 'Storage Services Configuration',
      description: 'Configure storage scanning settings here. Add storage buckets, set scan intervals, and manage storage scan schedules for S3 and other cloud storage services.',
      side: 'left',
      align: 'start'
    }
  },
  // Cloud Infra Tab
  {
    element: '[data-tour="manage-scans-cloud-infra-tab"]',
    popover: {
      title: 'Cloud Infrastructure Tab',
      description: 'Click here to manage cloud infrastructure scans. Configure IAM scans for AWS, Azure, GCP, and other cloud providers.',
      side: 'bottom',
      align: 'start'
    }
  },
  // Cloud Infra Panel
  {
    element: '[data-tour="manage-scans-cloud-infra-panel"]',
    popover: {
      title: 'Cloud Infrastructure Scanning',
      description: 'Manage IAM scans for your cloud infrastructure. View existing scan schedules, configure scan intervals, and monitor cloud security findings.',
      side: 'left',
      align: 'start'
    }
  },
  // Tour Complete
  {
    popover: {
      title: 'Tour Complete!',
      description: 'You\'ve completed the Manage Scans tour! You can always restart it by clicking the "Start Tour" button. Happy scanning!'
    }
  }
];

// Global click handler for driver buttons
let globalDriverClickHandler = null;

// Function to add skip button to driver popover
const addSkipButtonToPopover = (instance) => {
  // Find the driver popover footer where buttons are located
  const popover = document.querySelector('.driver-popover');
  if (!popover) return;

  // Find the footer element that contains the buttons
  const footer = popover.querySelector('.driver-popover-footer');
  if (!footer) return;

  // Check if skip button already exists and remove it if on last step
  const existingSkipButton = popover.querySelector('.driver-skip-button');
  if (existingSkipButton) {
    // Remove existing skip button if we're on last step
    const progressIndicatorCheck = footer.querySelector('.driver-popover-progress-text');
    if (progressIndicatorCheck) {
      const progressText = progressIndicatorCheck.textContent || '';
      const match = progressText.match(/(\d+)\s+of\s+(\d+)/i);
      if (match) {
        const currentStep = parseInt(match[1], 10);
        const totalSteps = parseInt(match[2], 10);
        if (currentStep === totalSteps) {
          existingSkipButton.remove();
        }
      }
    }
    // If skip button exists and we're not on last step, don't recreate
    if (existingSkipButton.parentNode) {
      return;
    }
  }

  // Find all elements in the footer
  const previousButton = Array.from(footer.querySelectorAll('button')).find(btn => {
    const text = btn.textContent?.toLowerCase().trim() || '';
    return text.includes('previous') || text.includes('prev') || btn.classList.contains('driver-prev-btn');
  });
  
  const nextButton = Array.from(footer.querySelectorAll('button')).find(btn => {
    const text = btn.textContent?.toLowerCase().trim() || '';
    return text.includes('next') || btn.classList.contains('driver-next-btn');
  });

  const progressIndicator = footer.querySelector('.driver-popover-progress-text');

  // Check if we're on the last step by parsing progress indicator
  let isLastStep = false;
  if (progressIndicator) {
    const progressText = progressIndicator.textContent || '';
    const match = progressText.match(/(\d+)\s+of\s+(\d+)/i);
    if (match) {
      const currentStep = parseInt(match[1], 10);
      const totalSteps = parseInt(match[2], 10);
      isLastStep = currentStep === totalSteps;
    }
  }

  // Get reference button to match styling
  const referenceButton = previousButton || nextButton;
  if (!referenceButton) return;

  // Only create skip button if not on last step
  let skipButton = null;
  if (!isLastStep) {
    // Get computed styles from reference button to match styling exactly
    const refStyles = window.getComputedStyle(referenceButton);
    
    // Get all relevant styles to ensure perfect consistency
    const buttonStyles = {
      padding: refStyles.padding,
      height: refStyles.height,
      minHeight: refStyles.minHeight,
      background: refStyles.backgroundColor,
      border: refStyles.border,
      borderWidth: refStyles.borderWidth,
      borderStyle: refStyles.borderStyle,
      borderColor: refStyles.borderColor,
      color: refStyles.color,
      borderRadius: refStyles.borderRadius,
      cursor: refStyles.cursor,
      fontSize: refStyles.fontSize,
      fontWeight: refStyles.fontWeight,
      fontFamily: refStyles.fontFamily,
      lineHeight: refStyles.lineHeight,
      display: refStyles.display,
      alignItems: refStyles.alignItems,
      justifyContent: refStyles.justifyContent,
      whiteSpace: refStyles.whiteSpace,
      margin: refStyles.margin,
      boxSizing: refStyles.boxSizing,
    };

    // Create skip button with exact same styling as other buttons
    skipButton = document.createElement('button');
    skipButton.className = 'driver-skip-button';
    skipButton.textContent = 'Skip Tour';
    skipButton.setAttribute('aria-label', 'Skip tour');
    
    // Apply all styles to match exactly
    Object.assign(skipButton.style, {
      padding: buttonStyles.padding,
      height: buttonStyles.height,
      minHeight: buttonStyles.minHeight,
      background: buttonStyles.background,
      border: buttonStyles.border,
      color: buttonStyles.color,
      borderRadius: buttonStyles.borderRadius,
      cursor: buttonStyles.cursor,
      fontSize: buttonStyles.fontSize,
      fontWeight: buttonStyles.fontWeight,
      fontFamily: buttonStyles.fontFamily,
      lineHeight: buttonStyles.lineHeight,
      display: buttonStyles.display || 'inline-flex',
      alignItems: buttonStyles.alignItems || 'center',
      justifyContent: buttonStyles.justifyContent || 'center',
      whiteSpace: buttonStyles.whiteSpace || 'nowrap',
      margin: buttonStyles.margin || '0',
      boxSizing: buttonStyles.boxSizing || 'border-box',
      transition: 'all 0.2s',
    });

    // Add hover effect matching other buttons exactly
    skipButton.addEventListener('mouseenter', () => {
      // Check if reference button has a hover state we can observe
      const hoverBg = refStyles.getPropertyValue('--hover-background') || 
                      (referenceButton.onmouseenter ? '#f3f4f6' : buttonStyles.background);
      skipButton.style.background = hoverBg;
    });
    skipButton.addEventListener('mouseleave', () => {
      skipButton.style.background = buttonStyles.background;
    });

    // Handle skip button click
    skipButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      skipTours();
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
  }

  // Restructure footer with flex layout: Previous | Progress | [Skip Tour, Next]
  // Set footer to flex with justify-between
  footer.style.cssText = `
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 8px;
    min-height: 40px;
  `;

  // Create left container for Previous button
  const leftContainer = document.createElement('div');
  leftContainer.style.cssText = 'display: flex; align-items: center; flex-shrink: 0;';
  if (previousButton) {
    leftContainer.appendChild(previousButton);
  }

  // Create center container for Progress indicator
  const centerContainer = document.createElement('div');
  centerContainer.style.cssText = 'display: flex; align-items: center; flex: 1; justify-content: center; min-width: 0;';
  if (progressIndicator) {
    // Ensure progress indicator displays on one line
    progressIndicator.style.whiteSpace = 'nowrap';
    centerContainer.appendChild(progressIndicator);
  }

  // Create right container for Skip Tour and Next buttons
  const rightContainer = document.createElement('div');
  rightContainer.style.cssText = 'display: flex; align-items: center; gap: 8px; flex-shrink: 0;';
  
  // Only add skip button if not on last step
  if (skipButton) {
    rightContainer.appendChild(skipButton);
  }
  
  if (nextButton) {
    rightContainer.appendChild(nextButton);
  }

  // Clear footer and add new structure
  footer.innerHTML = '';
  footer.appendChild(leftContainer);
  footer.appendChild(centerContainer);
  footer.appendChild(rightContainer);
};

const createDriverInstance = () => {
  // Remove existing global handler if any
  if (globalDriverClickHandler) {
    document.removeEventListener('click', globalDriverClickHandler, true);
    globalDriverClickHandler = null;
  }
  
  // Always create a fresh instance
  const instance = driver({
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    allowClose: true,
    overlayOpacity: 0.5,
    steps: getTourSteps(),
    onDestroyStarted: (element, step, options) => {
      localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
    },
    onDestroyed: (element, step, options) => {
      if (globalDriverClickHandler) {
        document.removeEventListener('click', globalDriverClickHandler, true);
        globalDriverClickHandler = null;
      }
      // Clean up after tour is destroyed
      if (driverInstance === instance) {
        driverInstance = null;
      }
    },
    onHighlightStarted: (element, step, options) => {
      // Add skip button to popover when tour starts highlighting
      setTimeout(() => addSkipButtonToPopover(instance), 50);
    },
    onHighlighted: (element, step, options) => {
      // Ensure skip button is added to popover
      setTimeout(() => addSkipButtonToPopover(instance), 50);
    },
    onStarted: () => {
      // Add skip button when tour starts
      setTimeout(() => addSkipButtonToPopover(instance), 50);
    }
  });
  
  // Set up global click handler for driver buttons
  globalDriverClickHandler = (e) => {
    const target = e.target;
    const button = target.closest('button');
    
    // Check if click is on skip button
    if (button && button.classList.contains('driver-skip-button')) {
      // Skip button handles its own click, but we can prevent default behavior
      return;
    }
    
    // Only handle clicks inside driver popover
    const popover = target.closest('.driver-popover');
    if (!popover) return;
    
    if (button) {
      const buttonText = button.textContent?.toLowerCase().trim() || '';
      const isDoneButton = buttonText.includes('done') || 
                          buttonText.includes('finish') ||
                          button.getAttribute('data-driver-done-button') !== null;
      const isCloseButton = buttonText.includes('×') || 
                           buttonText.includes('✕') ||
                           button.getAttribute('data-driver-close-button') !== null ||
                           button.classList.contains('driver-close-btn') ||
                           button.classList.contains('driver-popover-close-btn') ||
                           button.closest('.driver-popover-close-btn') !== null;
      
      if (isDoneButton || isCloseButton) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (instance && typeof instance.destroy === 'function') {
          instance.destroy();
        }
        return false;
      }
    }
  };
  
  // Add global click handler in capture phase to catch before driver.js handlers
  document.addEventListener('click', globalDriverClickHandler, true);
  
  return instance;
};

const initializeDriver = () => {
  // If instance exists and is active, destroy it first
  if (driverInstance) {
    try {
      driverInstance.destroy();
    } catch (e) {
      // Ignore errors if already destroyed
    }
    driverInstance = null;
  }

  // Create a fresh instance
  driverInstance = createDriverInstance();
  return driverInstance;
};

export const useManageScansTour = (autoStart = false) => {
  const driverObjRef = useRef(null);

  useEffect(() => {
    // Auto-start tour for first-time users
    if (autoStart) {
      const hasCompletedTour = localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
      const toursSkipped = isTourSkipped();
      if (!hasCompletedTour && !toursSkipped) {
        // Initialize driver.js
        const driverObj = initializeDriver();
        driverObjRef.current = driverObj;
        
        // Wait for DOM to be ready
        setTimeout(() => {
          driverObj.drive();
        }, 1000);
      }
    }

    return () => {
      // Don't destroy on unmount, just clear ref
      driverObjRef.current = null;
    };
  }, [autoStart]);

  const startTour = useCallback(() => {
    // Initialize and start tour with a fresh instance
    const driverObj = initializeDriver();
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      driverObj.drive();
    }, 100);
  }, []);

  return { startTour };
};

export default useManageScansTour;

